// frontend/src/components/landing/AgentTimeline.jsx
import { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PIPELINE_AGENTS } from "./content";
import "./AgentTimeline.css";

gsap.registerPlugin(ScrollTrigger);

/** "How it works": the eight agents as a vertical, scroll-revealed timeline. */
export default function AgentTimeline({ agents = PIPELINE_AGENTS }) {
  const rootRef = useRef(null);
  const trackRef = useRef(null);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const track = trackRef.current;
    if (!root || !track) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const ctx = gsap.context(() => {
      gsap.from(".timeline__intro > *", {
        opacity: 0,
        y: 24,
        duration: 0.9,
        ease: "power3.out",
        stagger: 0.08,
        scrollTrigger: { trigger: root, start: "top 80%", once: true },
      });
      gsap.from(".agent-step", {
        opacity: 0,
        y: 32,
        duration: 0.85,
        ease: "power3.out",
        stagger: 0.09,
        scrollTrigger: { trigger: track, start: "top 82%", once: true },
      });
      gsap.fromTo(
        ".timeline__rail-fill",
        { scaleY: 0 },
        {
          scaleY: 1,
          ease: "none",
          transformOrigin: "top center",
          scrollTrigger: { trigger: track, start: "top 70%", end: "bottom 65%", scrub: 0.6 },
        }
      );
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={rootRef} id="how-it-works" className="timeline section" aria-labelledby="how-title">
      <div className="container timeline__grid">
        <header className="timeline__intro">
          <p className="eyebrow">How it works</p>
          <h2 id="how-title" className="section__title">
            Eight agents.
            <br />
            <em>One conversation.</em>
          </h2>
          <p className="section__lede">
            You describe what you want in plain words. PAWNI routes it through a pipeline of specialists that
            understand, draft, evaluate and deliver. Each one hands its work to the next, and you can step in at
            any point.
          </p>
        </header>

        <div ref={trackRef} className="timeline__track">
          <span className="timeline__rail" aria-hidden="true">
            <span className="timeline__rail-fill" />
          </span>
          <ol className="timeline__list" role="list">
          {agents.map((a, i) => (
            <li key={a.key} className="agent-step" style={{ "--agent-c": `var(--agent-${a.key})` }}>
              <span className="agent-step__node" aria-hidden="true" />
              <div className="agent-step__meta">
                <span className="agent-step__num">{String(i + 1).padStart(2, "0")}</span>
                <span className="agent-step__phase">{a.phase}</span>
              </div>
              <h3 className="agent-step__name">{a.name}</h3>
              <p className="agent-step__desc">{a.desc}</p>
            </li>
          ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
